import React, { useEffect, useState } from 'react'
import {
    Button,
    Modal,
    Upload,
    Input,
    Tooltip,
    Typography,
    Form,
} from 'antd'
import { FileOutlined, FilePdfOutlined, InboxOutlined, PlusOutlined } from '@ant-design/icons'
import { uploadResume, uploadResumeS3Url } from '../service/candidate.service'
import { useDispatch, useSelector } from 'react-redux'
import { candidateById, candidateEdit } from '../store/candidate.slice'
import { PdfIcon } from '../../../assets/icons'
import DeletePopUp from '../../../components/deletePopUp/deletePopUp'

const { Dragger } = Upload
const { Title } = Typography
const initialState = {
    deleteModal: false,
    deleteButtonLoading: false,
}

const FileUploadModal = ({
    activeTab,
    category,
    preAttachmentList,
    isModalOpen,
    setIsModalOpen,
    currentData,
    setCurrantAttachments,
    show
}) => {
    const [uploadedFile, setUploadedFile] = useState(null)
    const [previewUrl, setPreviewUrl] = useState(null)
    const [loading, setLoading] = useState(false)
    const [state, setState] = useState(initialState)

    const [form] = Form.useForm()
    const dispatch = useDispatch()
    const { candidateId } = useSelector((state) => state.candidateSlice)

    const handleBeforeUpload = (file) => {
        setUploadedFile(file)
        return false
    }

    useEffect(() => {
        if (currentData) {
            setUploadedFile(null)
            form.setFieldsValue({ fieldName: currentData?.displayName })
        }
    }, [currentData, isModalOpen])

    useEffect(() => {
        let objectUrl = null
        const file = uploadedFile || currentData?.file
        if (uploadedFile instanceof File || uploadedFile instanceof Blob) {
            objectUrl = URL.createObjectURL(uploadedFile)
            setPreviewUrl(objectUrl)
        } else if (currentData?.file?.fileLink) {
            setPreviewUrl(currentData.file.fileLink)
        }

        return () => {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl)
            }
        }
    }, [uploadedFile, currentData, isModalOpen])

    const handleRemove = () => {
        setUploadedFile(null)
        setPreviewUrl(null)
    }

    const handleSaveNoteWithAttachment = async (
        candidateId,
        attachment,
        fieldName
    ) => {
        try {
            let uploadedLink = null

            if (attachment) {
                const payload = {
                    data: [
                        {
                            filename: attachment.name,
                            contentType: attachment.type,
                        },
                    ],
                }

                const { data } = await uploadResume(payload)

                if (data.success) {
                    const match = data.data.find(
                        (item) => item.filename === attachment.name
                    )

                    if (match) {
                        const response = await uploadResumeS3Url(
                            attachment,
                            match.uploadUrl,
                            match.publicLink,
                            match.name
                        )

                        if (response.ok) {
                            uploadedLink = {
                                title: fieldName,
                                displayName: fieldName,
                                category: category,
                                file: {
                                    fileName: attachment.name,
                                    fileLink: match.publicLink,
                                    fileType: attachment.type,
                                    uploadDate: Date.now(),
                                },
                            }
                        }
                    }
                }
            }

            await handleSubmit(
                candidateId,
                uploadedLink ? [uploadedLink] : null,
                fieldName
            )
            handleCancel()
        } catch (error) {
            console.error('Error saving note with attachment:', error)
        }
    }

    const handleDelete = async (id) => {
        try {
            const updatedList = preAttachmentList.filter(
                (item) => item._id !== id
            )

            const payload = {
                attachments: updatedList,
                candidateId,
            }

            const { data } = await candidateEdit(
                activeTab,
                candidateId,
                payload
            )
            if (data.success) {
                dispatch(candidateById({ id: candidateId, activeTab }))
                handleCancel()
            }
        } catch (error) {
            console.error('Delete failed:', error)
        }
    }

    const handleSubmit = async (candidateId, uploadedLink, fieldName) => {
        try {
            let allAttachments = []

            if (currentData && !uploadedLink) {
                allAttachments = preAttachmentList.map((item) => {
                    if (item._id === currentData._id) {
                        return {
                            ...item,
                            title: fieldName,
                            displayName: fieldName,
                        }
                    }
                    return item
                })
            } else {
                allAttachments = [
                    ...preAttachmentList.filter(
                        (item) => item._id !== currentData?._id
                    ),
                    ...(uploadedLink || []),
                ]
            }

            const payload = {
                attachments: allAttachments,
                candidateId,
            }

            const { data } = await candidateEdit(
                activeTab,
                candidateId,
                payload
            )

            if (data.success) {
                dispatch(candidateById({ id: candidateId, activeTab }))
            }
        } catch (error) {
            console.error('Submit error:', error)
        }
    }

    const handleCancel = () => {
        setIsModalOpen(false)
        setUploadedFile(null)
        setPreviewUrl(null)
        form.resetFields()
    }

    const handleModalOk = async () => {
        try {
            setLoading(true)
            const values = await form.validateFields()
            await handleSaveNoteWithAttachment(
                candidateId,
                uploadedFile,
                values.fieldName
            )
        } catch (errorInfo) {
            console.error('Validation Failed:', errorInfo)
        } finally {
            setLoading(false)
        }
    }

    const handleOpenModel = () => {
        setIsModalOpen(true)
        setCurrantAttachments(null)
        setUploadedFile(null)
        setPreviewUrl(null)
        form.resetFields()
    }

    const file = uploadedFile || currentData?.file
    const fileType = file?.type || file?.fileType || ''
    const isImage = fileType.startsWith('image/')
    const isPdf = fileType === 'application/pdf'

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <Title level={4}>Attachment</Title>
                {show && <Tooltip title="Add Attachment">
                    <button
                        style={{
                            width: '26px',
                            height: '26px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginLeft: '10px',
                            borderRadius: '50%',
                            border: 'none',
                        }}
                        onClick={handleOpenModel}
                    >
                        <PlusOutlined style={{ fontSize: '15px' }} />
                    </button>
                </Tooltip>}

            </div>

            <Modal
                title="Upload File"
                open={isModalOpen}
                onOk={handleModalOk}
                onCancel={handleCancel}
                footer={[
                    currentData ? (
                        <Button
                            key="delete"
                            onClick={() => {
                                setState(
                                    (
                                        prevState
                                    ) => ({
                                        ...prevState,
                                        deleteModal: true,

                                    })
                                )
                            }}
                            // onClick={() =>}
                            danger
                            style={{ backgroundColor: "transparent" }}
                        >
                            Delete
                        </Button>
                    ) : (
                        <Button style={{ marginRight: "5px", color: "#000", backgroundColor: "transparent", border: "1px solid #d9d9d9" }}
                            color="default" variant="outlined" key="cancel" onClick={handleCancel}>
                            Cancel
                        </Button>
                    ),
                    <Button
                        loading={loading}
                        key="submit"
                        onClick={handleModalOk}
                        disabled={!uploadedFile && !currentData}
                    >
                        {currentData ? 'Save' : 'Add'}
                    </Button>,
                ]}
            >
                <Form form={form} layout="vertical">
                    {!uploadedFile && !currentData ? (
                        <Dragger
                            name="file"
                            multiple={false}
                            beforeUpload={handleBeforeUpload}
                            onRemove={handleRemove}
                            style={{ marginBottom: 16 }}
                        >
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">
                                Click or drag a file here to upload
                            </p>
                        </Dragger>
                    ) : (
                        <>
                            <ul style={{ listStyle: 'none', padding: 0 }}>
                                <li
                                    key={file?.uid || file?.name || currentData?._id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        border: '1px solid #eee',
                                        padding: '10px',
                                        borderRadius: '6px',
                                        marginBottom: '10px',
                                    }}
                                >
                                    {isImage && previewUrl ? (
                                        <img
                                            src={previewUrl}
                                            alt="Preview"
                                            style={{
                                                width: 40,
                                                height: 40,
                                                objectFit: 'cover',
                                                borderRadius: '4px',
                                            }}
                                        />
                                    ) : isPdf ? (
                                        <FilePdfOutlined style={{ fontSize: 20, color: 'red' }} />
                                    ) : (
                                        <FileOutlined style={{ fontSize: 20, color: '#8c8c8c' }} />
                                    )}
                                    <span style={{ flex: 1, wordWrap: "break-word" }}>
                                        {file?.name || file?.fileName}
                                    </span>
                                    {!currentData && (
                                        <Button
                                            type="text"
                                            icon={<span style={{ fontSize: 16 }}>✖</span>}
                                            onClick={handleRemove}
                                            danger
                                        />
                                    )}
                                </li>
                            </ul>
                            <Form.Item
                                label="Attachment Title"
                                name="fieldName"
                                rules={[
                                    {
                                        required: true,
                                        message:
                                            'Please enter a title for the attachment',
                                    },
                                ]}
                            >
                                <Input placeholder="Enter your text here" />
                            </Form.Item>
                        </>
                    )}
                </Form>
            </Modal>
            <DeletePopUp
                previewDelete={state?.deleteModal}
                onDelete={() => {
                    setState((prevState) => ({
                        ...prevState,
                        deleteButtonLoading: true,
                    }))
                    handleDelete(currentData._id)
                }}
                loadingButton={state?.deleteButtonLoading}
                onCancel={() => {
                    setState((prevState) => ({
                        ...prevState,
                        deleteButtonLoading: false,
                        deleteModal: false,
                    }))
                }}
                modalTitle="Delete Attechment"
                pageKey="Comment"
            />
        </>
    )
}

export default FileUploadModal
